import _ from 'underscore';
import {combineReducers} from 'redux'
import {
   fetchAuction,
   prefetchAuction,
   getCachedAuction,
   fetchAuctionsForUser,
   fetchFilteredAuctions,
   prefetchFilteredAuctions,
   getCachedFilteredAuctions,
   fetchAuctioneers,
   prefetchAuctioneers,
   getCachedAuctioneers,
   fetchAuctioneer,
   prefetchAuctioneer,
   prefetchAuctionsForUser,
   fetchCategories,
   fetchExpertise
} from './gateways/auctionlook_api';
import {
   makeRaceProtectionContext,
   DEFAULT_AUCTIONS_PER_PAGE
} from './utils/gateways';
import {
    getFeature,
    FEATURE__SHOWCASE
} from './utils/features';

// pattern taken from:  https://github.com/erikras/ducks-modular-redux

/**
 * constants
 */

const RESOURCE_AUCTION = 'auction';
const RESOURCE_AUCTIONS = 'auctions';
const RESOURCE_AUCTIONEER = 'auctioneer';
const RESOURCE_AUCTIONEERS = 'auctioneers';
const RESOURCE_CATEGORIES = 'categories';
const RESOURCE_EXPERTISE = 'expertise';

const RACE_PROTECTION_CONTEXTS = {};

/**
 * action types
 */

export const GET_RESOURCE_START = 'auctionlook/ducks/GET_RESOURCE_START';
export const GET_RESOURCE_SUCCESS = 'auctionlook/ducks/GET_RESOURCE_SUCCESS';
export const GET_RESOURCE_FAILURE = 'auctionlook/ducks/GET_RESOURCE_FAILURE';
export const SET_PREMIUM_FILTER = 'auctionlook/ducks/SET_PREMIUM_FILTER';
export const CLEAR_RESOURCE = 'auctionlook/ducks/CLEAR_RESOURCE';
export const SET_LAST_LIST_URL = 'auctionlook/ducks/SET_LAST_LIST_URL';

/**
 * action creators
 */

export const getResourceStart = (resourceType) => ({
   type: GET_RESOURCE_START,
   resourceType
});
export const getResourceFailure = (resourceType, error, errorDetail) => ({
   type: GET_RESOURCE_FAILURE,
   resourceType,
   payload: {error, errorDetail}
});
export const getResourceSuccess = (resourceType, data) => ({
   type: GET_RESOURCE_SUCCESS,
   resourceType,
   payload: {data}
});

export const setPremiumFilter = (premium) => ({
   type: SET_PREMIUM_FILTER,
   payload: premium
});

export const setLastListUrl = (url) => ({
   type: SET_LAST_LIST_URL,
   payload: url
});

export const clearResource = (resourceType) => ({
   type: CLEAR_RESOURCE,
   resourceType
});



// thunk
export const prepareResource = (resource, fetcher) => (
   (dispatch, getState) => {
      const { instanceId } = getState();
      dispatch(getResourceStart(resource));
      return fetcher()
         .then(
            RACE_PROTECTION_CONTEXTS[instanceId](
               resource,
               dispatch,
               getResourceSuccess.bind(null, resource)
            )
         )
         .catch(
            error => dispatch(getResourceFailure(resource, `Unable to get resource: ${resource}`, error))
         );
   }
);

export const prepareFilteredAuctions = (page, category, region, date, past) => (
   (dispatch, getState) => {
      const {
         associationState: association,
         premiumFilter: premium,
         auctionsPerPage: pageSize,
      } = getState();
      const filters = {
         state: region,
         association,
         premium,
         categories: [category],
         date,
         past,
      };

      // Check if data is cached - if so, use it immediately without loading state
      var cachedData = getCachedFilteredAuctions({filters, page, pageSize});
      if (cachedData) {
         dispatch(getResourceSuccess(RESOURCE_AUCTIONS, cachedData));
         return Promise.resolve(cachedData);
      }

      return dispatch(prepareResource(
         RESOURCE_AUCTIONS,
         () => fetchFilteredAuctions({filters, page, pageSize})
      ));
   }
);

// Prefetch auctions page in background (no loading state, just warm cache)
export const prefetchAuctionsPage = (page, category, region, date, past) => (
   (dispatch, getState) => {
      const {
         associationState: association,
         premiumFilter: premium,
         auctionsPerPage: pageSize,
      } = getState();
      const filters = {
         state: region,
         association,
         premium,
         categories: [category],
         date,
         past,
      };

      // Fire and forget - just warm the cache
      prefetchFilteredAuctions({filters, page, pageSize});
   }
);

// Prefetch premium auctions on hover (for instant toggle)
export const prefetchPremiumAuctions = (page, category, region, date, past) => (
   (dispatch, getState) => {
      const {
         associationState: association,
         auctionsPerPage: pageSize,
      } = getState();
      const filters = {
         state: region,
         association,
         premium: 'y', // Always prefetch premium
         categories: [category],
         date,
         past,
      };

      // Fire and forget - just warm the cache
      prefetchFilteredAuctions({filters, page, pageSize});
   }
);

export const prepareAuctionsForAuctioneer = (auctioneerId) => (
   (dispatch) => {
      return dispatch(prepareResource(
         RESOURCE_AUCTIONS,
         () => fetchAuctionsForUser(auctioneerId)
      ));
   }
);

export const prepareAuction = (auctionId) => (
   (dispatch) => {
      // Check if data is cached - if so, use it immediately without loading state
      var cachedData = getCachedAuction(auctionId);
      if (cachedData) {
         dispatch(getResourceSuccess(RESOURCE_AUCTION, cachedData));
         return Promise.resolve(cachedData);
      }

      return dispatch(prepareResource(
         RESOURCE_AUCTION,
         () => fetchAuction(auctionId)
      ));
   }
);

// Prefetch auction data in background (for hover prefetch on cards)
export const prefetchAuctionData = (auctionId) => () => {
   prefetchAuction(auctionId);
};

export const prepareAuctioneer = (auctioneerId) => (
   (dispatch) => {
      return dispatch(prepareResource(
         RESOURCE_AUCTIONEER,
         () => fetchAuctioneer(auctioneerId)
      ));
   }
);

// Prefetch auctioneer data in background (for hover prefetch on cards)
export const prefetchAuctioneerData = (auctioneerId) => () => {
   // Prefetch both the auctioneer profile and their auctions
   prefetchAuctioneer(auctioneerId);
   prefetchAuctionsForUser(auctioneerId);
};

export const prepareAuctioneers = (page, expertise, search, association) => (
   (dispatch, getState) => {
      const state = getState();
      let filters = {
         expertise,
         association: state.associationState || association,
         search,
      };

      if (search) {
         // if we're searching, the results should be ordered consistently
         filters = {
            sort: 'asc',
            ...filters,
         };
      }

      // Check if data is cached - if so, use it immediately without loading state
      var cachedData = getCachedAuctioneers(filters, page);
      if (cachedData) {
         dispatch(getResourceSuccess(RESOURCE_AUCTIONEERS, cachedData));
         return Promise.resolve(cachedData);
      }

      return dispatch(prepareResource(
         RESOURCE_AUCTIONEERS,
         () => fetchAuctioneers(filters, page)
      ));
   }
);

// Prefetch auctioneers page in background (no loading state, just warm cache)
export const prefetchAuctioneersPage = (page, expertise, search, association) => (
   (dispatch, getState) => {
      const state = getState();
      let filters = {
         expertise,
         association: state.associationState || association,
         search,
      };

      if (search) {
         filters = {
            sort: 'asc',
            ...filters,
         };
      }

      // Fire and forget - just warm the cache
      prefetchAuctioneers(filters, page);
   }
);

export const prepareCategories = () => (
   (dispatch) => {
      return dispatch(prepareResource(
         RESOURCE_CATEGORIES,
         fetchCategories
      ));
   }
);

export const prepareExpertise = () => (
   (dispatch) => {
      return dispatch(prepareResource(
         RESOURCE_EXPERTISE,
         fetchExpertise
      ));
   }
);

export const prepareAuctioneerPage = (auctioneerId) => (dispatch, getState) => {
   let {lockToAuctioneer, auctioneer} = getState();
   const targetId = auctioneerId || lockToAuctioneer;

   // Clear old auctioneer data if navigating to a different auctioneer
   // This prevents stale data from showing during load
   if (auctioneer && auctioneer.data && String(auctioneer.data.userId) !== String(targetId)) {
      dispatch(clearResource(RESOURCE_AUCTIONEER));
      dispatch(clearResource(RESOURCE_AUCTIONS));
   }

   // by doing lockToAuctioneer this way, we can still override it
   // from the match params
   dispatch(prepareAuctioneer(targetId));
   dispatch(prepareAuctionsForAuctioneer(targetId));
};

/**
 * reducers
 */

const makeLoadingReducer = (targetResourceType) => (
   (state = false, {type, resourceType}) => {
      if (resourceType !== targetResourceType) {
         return state;
      }
      if (type === GET_RESOURCE_START) {
         return true;
      }
      if (type === GET_RESOURCE_SUCCESS || type === GET_RESOURCE_FAILURE) {
         return false;
      }
      return state;
   }
);

const makeResourcePayloadReducer = (targetResourceType, defaultState) => (
   (state = defaultState, {type, resourceType, payload}) => {
      if (resourceType !== targetResourceType) {
         return state;
      }
      if (type === CLEAR_RESOURCE) {
         // Immediately clear stale data when navigating to new entity
         return defaultState;
      }
      if (type === GET_RESOURCE_START) {
         // Keep old data visible while loading new data for better perceived performance.
         // Only clear if we have no existing data (initial load).
         return state || defaultState;
      }
      if (type === GET_RESOURCE_SUCCESS) {
         return payload.data;
      }
      return state;
   }
);

const makeResourceErrorReducer = (targetResourceType) => (
   (state = null, {type, resourceType, payload}) => {
      if (resourceType !== targetResourceType) {
         return state;
      }
      if (type === GET_RESOURCE_START) {
         return null;
      }
      if (type === GET_RESOURCE_FAILURE) {
         return _(payload).pick('error', 'errorDetail');
      }

      return state;
   }
);

const makeResourceReducer = (resourceType, defaultData) => (
   combineReducers({
      loading: makeLoadingReducer(resourceType),
      data: makeResourcePayloadReducer(resourceType, defaultData),
      error: makeResourceErrorReducer(resourceType)
   })
);

const makeImmutable = (defaultValue) => (state = defaultValue) => state;

// Premium filter reducer - can be toggled by user
const makePremiumFilterReducer = (defaultValue) => (state = defaultValue, action) => {
   if (action.type === SET_PREMIUM_FILTER) {
      return action.payload;
   }
   return state;
};

// Last auctions list URL reducer - remembers the last list the user was on
const lastListUrlReducer = (state = null, action) => {
   if (action.type === SET_LAST_LIST_URL) {
      return action.payload;
   }
   return state;
};


let instanceId = 1;
export default () => {
   const id = `${++instanceId}`;

   // did this so we coudl have more than one showcase on the front
   RACE_PROTECTION_CONTEXTS[id] = makeRaceProtectionContext();

   return combineReducers({
      instanceId: makeImmutable(id),
      auction: makeResourceReducer(RESOURCE_AUCTION, null),
      auctions: makeResourceReducer(RESOURCE_AUCTIONS, null),
      auctioneer: makeResourceReducer(RESOURCE_AUCTIONEER, null),
      auctioneers: makeResourceReducer(RESOURCE_AUCTIONEERS, null),
      categories: makeResourceReducer(RESOURCE_CATEGORIES, []),
      expertise: makeResourceReducer(RESOURCE_EXPERTISE, []),
      // these hoisted on state when app is created
      associationState: makeImmutable(''),
      lockToAuctioneer: makeImmutable(null),
      premiumFilter: makePremiumFilterReducer(null),
      auctionsPerPage: makeImmutable(DEFAULT_AUCTIONS_PER_PAGE),
      lockCategory: makeImmutable(''),
      lastListUrl: lastListUrlReducer,
   });
};
